/**
 * Privacy Consent Banner Alpine.js Component
 *
 * Manages privacy consent preferences with support for:
 * - GDPR (opt-in) and CCPA/VCDPA/CPA (opt-out) compliance models
 * - Google Consent Mode v2 integration
 * - Meta Pixel consent signaling
 * - Global Privacy Control (GPC) signal detection
 * - Deferred script loading based on consent
 */
export default function privacyConsent(config) {
    return {
        // State
        showBanner: false,
        isModalOpen: false,
        config: config,
        consent: {},

        // Lifecycle
        init() {
            this.loadConsent();
            this.checkGpcSignal();
            this.determineVisibility();
            this.setupEventListeners();

            this.$watch('isModalOpen', open => {
                document.documentElement.style.overflow = open ? 'hidden' : '';
            });
        },

        // Event listeners
        setupEventListeners() {
            // Listen for open-privacy-preferences event (from footer link)
            window.addEventListener('open-privacy-preferences', () => {
                this.openModal();
            });

            // Also support Alpine's $dispatch
            this.$el.addEventListener('open-privacy-preferences', () => {
                this.openModal();
            });
        },

        // Cookie management
        loadConsent() {
            const cookieValue = this.getCookie(this.config.cookieName);
            if (cookieValue) {
                try {
                    const parsed = JSON.parse(cookieValue);
                    if (parsed.categories && parsed.version) {
                        this.consent = parsed.categories;
                        // If consent version differs, may need to re-prompt
                        if (parsed.version !== this.config.consentVersion) {
                            // New consent version, clear old consent
                            this.consent = {};
                        }
                    }
                } catch (e) {
                    // Invalid cookie, start fresh
                    this.consent = {};
                }
            }
        },

        saveConsent() {
            const cookieData = {
                version: this.config.consentVersion,
                timestamp: new Date().toISOString(),
                categories: this.consent
            };

            this.setCookie(
                this.config.cookieName,
                JSON.stringify(cookieData),
                this.config.cookieExpiryDays
            );

            // Update third-party consent signals
            this.updateGoogleConsent();
            this.updateMetaConsent();
            this.updateSalesforceConsent();

            // Dispatch event for any listeners
            this.$dispatch('privacy-consent-updated', { consent: this.consent });
            window.dispatchEvent(new CustomEvent('privacyConsentUpdated', {
                detail: { consent: this.consent }
            }));

            // Load any deferred scripts/pixels that now have consent
            this.loadDeferredScripts();
            this.loadDeferredPixels();
        },

        // GPC (Global Privacy Control) support — overrides prior consent per CPA
        checkGpcSignal() {
            if (navigator.globalPrivacyControl === true) {
                this.consent.marketing = false;
            }
        },

        // Banner visibility
        determineVisibility() {
            if (!this.config.enabled) {
                this.showBanner = false;
                return;
            }

            // Always show essential as granted
            this.consent.essential = true;

            const hasExistingConsent = Object.keys(this.consent).length > 1;

            if (hasExistingConsent) {
                // User has already made a choice
                this.showBanner = false;
                // Still apply their consent
                this.updateGoogleConsent();
                this.updateMetaConsent();
                this.updateSalesforceConsent();
                this.loadDeferredScripts();
                this.loadDeferredPixels();
            } else {
                // No consent recorded, show banner
                this.showBanner = true;

                // For opt-out mode, default to granted (user can opt out)
                if (this.config.mode === 'opt_out' || this.config.mode === 'notice') {
                    this.consent = this.getAllCategoriesGranted();
                    // GPC signal overrides defaults for marketing
                    this.checkGpcSignal();

                    // Apply consent signals and activate any deferred scripts
                    // immediately — opt-out/notice visitors have implied consent
                    // for all non-GPC categories, so deferred scripts that the
                    // server rendered (e.g. marketing scripts blocked by GPC)
                    // should be activated for the categories the visitor allows.
                    this.updateGoogleConsent();
                    this.updateMetaConsent();
                    this.updateSalesforceConsent();
                    this.loadDeferredScripts();
                    this.loadDeferredPixels();
                }
            }
        },

        // Consent actions
        acceptAll() {
            this.consent = this.getAllCategoriesGranted();
            this.checkGpcSignal();
            this.saveConsent();
            this.closeBanner();
        },

        rejectNonEssential() {
            this.consent = this.getEssentialOnlyConsent();
            this.saveConsent();
            this.closeBanner();
        },

        savePreferences() {
            // Ensure essential is always granted
            this.consent.essential = true;
            this.checkGpcSignal();
            this.saveConsent();
            this.closeModal();
            this.closeBanner();
        },

        // Modal management
        openModal() {
            // Initialize consent toggles with current state or defaults
            if (Object.keys(this.consent).length <= 1) {
                // No prior consent, set defaults based on mode
                if (this.config.mode === 'opt_in') {
                    this.consent = this.getEssentialOnlyConsent();
                } else {
                    this.consent = this.getAllCategoriesGranted();
                }
            }
            this.isModalOpen = true;
            this.trapFocus();
        },

        closeModal() {
            this.isModalOpen = false;
            this.releaseFocus();
        },

        closeBanner() {
            this.showBanner = false;
        },

        // Focus management for accessibility
        trapFocus() {
            // Store the element that was focused before opening modal
            this._previouslyFocused = document.activeElement;

            // Focus the first focusable element in the modal
            this.$nextTick(() => {
                const modal = this.$refs.modal;
                if (modal) {
                    const focusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                    if (focusable) focusable.focus();
                }
            });
        },

        releaseFocus() {
            // Return focus to previously focused element
            if (this._previouslyFocused) {
                this._previouslyFocused.focus();
            }
        },

        handleKeydown(event) {
            if (event.key === 'Escape') {
                if (this.isModalOpen) {
                    this.closeModal();
                }
            }
        },

        // Helper methods
        getAllCategoriesGranted() {
            const consent = { essential: true };
            Object.keys(this.config.categories).forEach(category => {
                consent[category] = true;
            });
            return consent;
        },

        getEssentialOnlyConsent() {
            const consent = { essential: true };
            Object.keys(this.config.categories).forEach(category => {
                consent[category] = category === 'essential';
            });
            return consent;
        },

        hasConsent(category) {
            if (category === 'essential') return true;
            return this.consent[category] === true;
        },

        toggleCategory(category) {
            if (category === 'essential') return; // Can't toggle essential
            this.consent[category] = !this.consent[category];
        },

        // Third-party consent updates
        updateGoogleConsent() {
            if (typeof gtag !== 'function') return;

            const analyticsGranted = this.hasConsent('analytics');
            const marketingGranted = this.hasConsent('marketing');

            gtag('consent', 'update', {
                'analytics_storage': analyticsGranted ? 'granted' : 'denied',
                'ad_storage': marketingGranted ? 'granted' : 'denied',
                'ad_user_data': marketingGranted ? 'granted' : 'denied',
                'ad_personalization': marketingGranted ? 'granted' : 'denied'
            });
        },

        updateMetaConsent() {
            if (typeof fbq !== 'function') return;

            const marketingGranted = this.hasConsent('marketing');
            fbq('consent', marketingGranted ? 'grant' : 'revoke');
        },

        updateSalesforceConsent() {
            const analyticsGranted = this.hasConsent('analytics');

            // Record the desired status so the inline init poller in
            // dataCloudScript.html.twig sees revocations that land
            // before SalesforceInteractions finishes downloading.
            window.__vdcSalesforceConsentStatus = analyticsGranted ? 'OptIn' : 'OptOut';

            if (typeof SalesforceInteractions === 'undefined') return;
            if (typeof SalesforceInteractions.updateConsents !== 'function') return;

            SalesforceInteractions.updateConsents({
                provider: 'vacatia',
                purpose: SalesforceInteractions.ConsentPurpose.Tracking,
                status: analyticsGranted
                    ? SalesforceInteractions.ConsentStatus.OptIn
                    : SalesforceInteractions.ConsentStatus.OptOut
            });
        },

        // Deferred script loading
        loadDeferredScripts() {
            const scripts = document.querySelectorAll('script[type="text/plain"][data-consent-category]');

            scripts.forEach(script => {
                const category = script.getAttribute('data-consent-category');
                if (this.hasConsent(category)) {
                    this.activateScript(script);
                }
            });
        },

        activateScript(script) {
            const newScript = document.createElement('script');

            // Copy attributes
            Array.from(script.attributes).forEach(attr => {
                if (attr.name !== 'type' && attr.name !== 'data-consent-category') {
                    if (attr.name === 'data-src') {
                        newScript.src = attr.value;
                    } else {
                        newScript.setAttribute(attr.name, attr.value);
                    }
                }
            });

            // Copy inline content if no src
            if (!newScript.src && script.textContent) {
                newScript.textContent = script.textContent;
            }

            // Replace the old script
            script.parentNode.replaceChild(newScript, script);
        },

        // Deferred pixel loading (for tracking pixels)
        loadDeferredPixels() {
            const pixels = document.querySelectorAll('img[data-consent-category][data-src]');

            pixels.forEach(pixel => {
                const category = pixel.getAttribute('data-consent-category');
                if (this.hasConsent(category)) {
                    this.activatePixel(pixel);
                }
            });
        },

        activatePixel(pixel) {
            const src = pixel.getAttribute('data-src');
            if (src) {
                pixel.src = src;
                pixel.removeAttribute('data-src');
                pixel.removeAttribute('data-consent-category');
            }
        },

        // Cookie utilities
        getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) {
                return decodeURIComponent(parts.pop().split(';').shift());
            }
            return null;
        },

        setCookie(name, value, days) {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));

            // Set cookie with security attributes
            const cookieParts = [
                `${name}=${encodeURIComponent(value)}`,
                `expires=${expires.toUTCString()}`,
                'path=/',
                'SameSite=Lax'
            ];

            // Add Secure flag for HTTPS
            if (window.location.protocol === 'https:') {
                cookieParts.push('Secure');
            }

            // Add domain for cross-subdomain support
            const domain = this.getRootDomain();
            if (domain) {
                cookieParts.push(`domain=${domain}`);
            }

            document.cookie = cookieParts.join('; ');
        },

        getRootDomain() {
            const hostname = window.location.hostname;
            if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
                return null;
            }
            // Extract root domain (e.g., vacatia.com from dev.vacatia.com)
            const parts = hostname.split('.');
            if (parts.length >= 2) {
                return '.' + parts.slice(-2).join('.');
            }
            return null;
        }
    };
}
